import { Camera, Clock, Database, Keyboard, MonitorSmartphone, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useUserPolicy } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { errorText } from "@/lib/errors";

/** Plain-language description of what the tracker records about a user. */
export function PolicySummary({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const policy = useUserPolicy(userId);
  if (policy.isPending) return <Skeleton className="h-40" />;
  if (policy.isError) return <Alert variant="danger">{errorText(t, policy.error)}</Alert>;
  const p = policy.data.effective;

  const lines: { icon: LucideIcon; text: string[] }[] = [
    {
      icon: Camera,
      text:
        p.screenshots_per_slot === 0
          ? [t("settings.policy.mine.screenshotsOff")]
          : [
              t("settings.policy.mine.screenshots", { count: p.screenshots_per_slot }),
              ...(p.screenshot_blur === "blur" ? [t("settings.policy.mine.blurred")] : []),
              ...(p.notify_on_screenshot ? [t("settings.policy.mine.notified")] : []),
            ],
    },
    {
      icon: MonitorSmartphone,
      text: p.track_apps
        ? [
            t("settings.policy.mine.apps"),
            ...(p.track_window_titles ? [t("settings.policy.mine.titles")] : []),
            t(`settings.policy.mine.urls.${p.track_urls}`),
          ]
        : [t("settings.policy.mine.appsOff")],
    },
    { icon: Keyboard, text: [t("settings.policy.mine.keyboard")] },
    {
      icon: Clock,
      text: [t(`settings.policy.mine.idle.${p.idle_action}`, { minutes: p.idle_threshold_minutes })],
    },
    { icon: Database, text: [t("settings.policy.mine.retention", { count: p.retention_days })] },
  ];

  return (
    <ul className="flex flex-col gap-3">
      {lines.map(({ icon: Icon, text }) => (
        <li key={text[0]} className="flex gap-3">
          <Icon className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
          <span>{text.join(" ")}</span>
        </li>
      ))}
    </ul>
  );
}
